import { FC, useContext } from "react";
import { Box, Card } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { FiberManualRecord as FiberManualRecordIcon } from "@material-ui/icons";
import { get } from "lodash";

import { Button, CardActionArea } from "components";
import { EMPTY_VALUE_PLACEHOLDER } from "constants/index";
import { AppContext } from "contexts";
import { formatCurrencyAmount, formatMarketplaceMedian } from "lib/format";
import * as routes from "routes";
import { getPageURL } from "routes/config";
import { ListingWithIncludes } from "services/listings/types";
import { NON_FAILED_DEAL_STATE_TO_DISPLAY_NAME } from "services/offers/constants";

const useStyles = makeStyles(theme => ({
  offersAwaitingAcceptanceButton: {
    color: theme.custom.palette.green.main,
    backgroundColor: theme.custom.palette.green.light,
    fontWeight: theme.typography.fontWeightMedium,
    pointerEvents: "none",
  },
  subHeader: {
    fontSize: "0.75rem",
    width: "100%",
    display: "flex",
    alignItems: "center",
  },
  fiberManualRecordIcon: {
    marginLeft: "-3px",
    color: (props: { available: boolean }) =>
      props.available
        ? theme.custom.palette.green.main
        : theme.custom.palette.gray.main,
  },
}));

export interface ListingCardProps {
  listing: ListingWithIncludes;
}

const ListingCard: FC<ListingCardProps> = ({ listing }) => {
  const classes = useStyles({ available: listing?.display_state?.available });
  const { domainNames } = useContext(AppContext);
  const isAcceptingFinalOffers = listing.state === "accepting_final_offers";

  const trackCount = listing.track_count
    ? listing.track_count.toString()
    : EMPTY_VALUE_PLACEHOLDER;

  const stats = [
    {
      label: listing?.deal ? "Sale Price" : "Your List Price",
      value:
        listing.buy_now_price || listing?.deal
          ? formatCurrencyAmount(
              "USD",
              listing?.deal ? listing?.deal.amount : listing.buy_now_price,
              0,
            )
          : EMPTY_VALUE_PLACEHOLDER,
    },
    {
      label: "Median Sale Price",
      value: listing.marketplace_median
        ? formatMarketplaceMedian(listing.marketplace_median)
        : EMPTY_VALUE_PLACEHOLDER,
    },
    { label: "Selected Songs", value: trackCount },
    {
      label: "Offers Received",
      value:
        listing.offers_received_count.toString() || EMPTY_VALUE_PLACEHOLDER,
    },
  ];

  return (
    <a
      href={getPageURL(
        routes.orderBookAssetDetail(listing.id.toString()),
        domainNames,
      )}
    >
      <Card>
        <CardActionArea
          action={{
            label: listing.display_state.available
              ? "View Analysis & Listing"
              : "View Analysis",
          }}
          cardHeader={{
            title: listing.title,
            subHeader: (
              <Box className={classes.subHeader}>
                <FiberManualRecordIcon
                  className={classes.fiberManualRecordIcon}
                  fontSize="small"
                />
                {listing?.deal?.state
                  ? get(
                      NON_FAILED_DEAL_STATE_TO_DISPLAY_NAME,
                      listing?.deal?.state,
                      EMPTY_VALUE_PLACEHOLDER,
                    )
                  : listing.display_state.message}
              </Box>
            ),
          }}
          topRightAction={
            isAcceptingFinalOffers ? (
              <Button className={classes.offersAwaitingAcceptanceButton}>
                Accepting Final Offers
              </Button>
            ) : (
              listing.offers_awaiting_acceptance && (
                <Button className={classes.offersAwaitingAcceptanceButton}>
                  New Offers Awaiting Review
                </Button>
              )
            )
          }
          stats={stats}
        />
      </Card>
    </a>
  );
};

export default ListingCard;
